/**
 * Runs `frame` on every animation frame while `el` is on screen and the tab is visible, and stops
 * otherwise, so a scene scrolled out of view costs nothing.
 */
export function whileVisible(el: Element, frame: (now: number) => void): () => void {
  let raf = 0;
  let onScreen = false;

  const tick = (now: number) => {
    frame(now);
    raf = requestAnimationFrame(tick);
  };

  const sync = () => {
    const run = onScreen && document.visibilityState === "visible";

    if (run && raf === 0) raf = requestAnimationFrame(tick);

    if (!run && raf !== 0) {
      cancelAnimationFrame(raf);
      raf = 0;
    }
  };

  /* A busy page delivers several changes in one batch, oldest first. The newest says where the
     element is now, and the first of a batch that went off and came back says it is gone. */
  const observer = new IntersectionObserver((entries) => {
    onScreen = entries.at(-1)?.isIntersecting ?? false;
    sync();
  });

  observer.observe(el);
  document.addEventListener("visibilitychange", sync);

  return () => {
    observer.disconnect();
    document.removeEventListener("visibilitychange", sync);
    cancelAnimationFrame(raf);
  };
}
